import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Button, Divider, Grid, IconButton } from '@mui/material';
import { grey } from '@mui/material/colors';
import { useState } from 'react';
import { AutomatedInsightSections } from '../../modules/automated-insights/constants';
import QuestionsChat from './QuestionsChat';
import UpgradeSubscriptionPrompt from './UpgradeSubscriptionPrompt';

const ExpandCollapse = ({ handleButtonClick }) => {
  return (
    <Box display="flex" alignItems="center">
      <Button
        variant="text"
        onClick={() => handleButtonClick(true)}
        sx={{
          fontWeight: 400,
          fontSize: '12px',
          color: (theme) => theme.palette.text.primary,
          textTransform: 'none',
          '&:hover': {
            fontWeight: 500,
          },
        }}>
        Expand all
      </Button>
      <Divider orientation="vertical" variant="middle" flexItem sx={{ borderColor: grey[400] }} />
      <Button
        variant="text"
        onClick={() => handleButtonClick(false)}
        sx={{
          fontWeight: 400,
          fontSize: '12px',
          color: (theme) => theme.palette.text.primary,
          textTransform: 'none',
          '&:hover': {
            fontWeight: 500,
          },
        }}>
        Collapse all
      </Button>
    </Box>
  );
};

// ownerUserId is the user id of the owner of the pulse, while chatUserId is the user that is logged in
// that is trying to send a chat message (based on a share link).
// This will be empty if the user is not logged in.
const ChatDrawer = ({
  sessionId,
  selectedSegment,
  ownerUserId,
  chatUserId,
  onClose,
  sessionType,
  isLockedFeature,
  selectedQuantTheme = null,
}) => {
  const [expanded, setExpanded] = useState(true);

  const [chatMessages, setChatMessages] = useState([]);
  const [openChatMessages, setOpenChatMessages] = useState(Array(999).fill(true).flat());
  const [showExpandCollapse, setShowExpandCollapse] = useState(false);

  const handleButtonClick = (openAll) => {
    if (openAll) {
      setOpenChatMessages(Array(chatMessages?.length).fill(true).flat());
    } else {
      setOpenChatMessages([]);
    }
  };

  const handleOpenMessageAccordion = (index, open) => {
    const array = [...openChatMessages];
    array[index] = open;
    setOpenChatMessages(array);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  // For pulse owner, if the current selected tab is not part of the user's tier, we block this tab and display a blocking message.
  // If the selected tab is Audience, we don't show this message.
  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: '20px',
        backgroundColor: '#FFFFFF',
      }}>
      <Grid
        container
        flexDirection="column"
        sx={{
          height: '100%',
          borderRadius: '20px',
          overflow: 'hidden',
          flexWrap: 'nowrap',
        }}>
        <Grid
          item
          sx={{
            minHeight: '50px',
            width: '100%',
            padding: 1,
            position: 'relative', // prevents the box shadow from being hidden by the element below it
            boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.1)',
          }}>
          <Box display="flex" alignItems="center" justifyContent="end" columnGap={1}>
            {showExpandCollapse && !isLockedFeature && (
              <ExpandCollapse
                expanded={expanded}
                onExpand={handleExpandClick}
                handleButtonClick={handleButtonClick}
              />
            )}
            <IconButton aria-label="close" size="small" onClick={() => onClose(false)}>
              <CloseRoundedIcon sx={{ fontSize: '18px', color: 'black' }} />
            </IconButton>
          </Box>
        </Grid>
        <Grid item sx={{ flexGrow: 1, overflowY: isLockedFeature ? 'auto' : 'visible' }}>
          {isLockedFeature ? (
            <Box display="flex" alignItems="center" justifyContent="center" height="100%">
              <UpgradeSubscriptionPrompt blockedFeature={AutomatedInsightSections.Chat} />
            </Box>
          ) : (
            <QuestionsChat
              sessionId={sessionId}
              selectedSegment={selectedSegment}
              ownerUserId={ownerUserId}
              chatUserId={chatUserId}
              showToolbar={true}
              showCarousel={true}
              selectedQuantTheme={selectedQuantTheme}
              sessionType={sessionType}
              setChatMessages={setChatMessages}
              setShowExpandCollapse={setShowExpandCollapse}
              openChatMessages={openChatMessages}
              handleOpenMessageAccordion={handleOpenMessageAccordion}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ChatDrawer;
